/**
 * Main menu. A live radar scope of the last airport fills the screen and the menu entries are
 * flight progress strips. The background sim runs at low realism with no controller, for
 * atmosphere only. A first visit (nothing saved, no lesson passed) gets the landing variant: what
 * the game is, a sample exchange on frequency, and one obvious first step — the tutorial.
 */
import { createRadarScope, type RadarScope } from '@atc/radar';
import { createSim, type RadioMessage } from '@atc/sim';
import { LESSONS } from '../academy/lessons';
import { AIRPORTS, airportEntry, loadAirport } from '../airports';
import { mountMenuNavigation } from '../menu-navigation';
import { trackEvent } from '../analytics';
import { createRadio, primeRadioPlayback, type NoiseLevel } from '../radio';

/** A real exchange from lesson 1's airspace, as the comms log would show it. */
const SAMPLE: [who: string, text: string][] = [
  ['ELY312', 'Ben Gurion Approach, El Al three one two, descending one zero thousand, information Kilo.'],
  ['YOU', 'El Al three one two, radar contact. Descend and maintain six thousand, turn left heading two seven zero.'],
  ['ELY312', 'Descend six thousand, left heading two seven zero, El Al three one two.'],
];

/** How the radio sounds in a game, so the comm check sounds the same. Absent: playback is off on this server. */
export interface CommCheckRadio { provider: 'browser' | 'openai'; pace: 'normal' | 'brisk'; noise: NoiseLevel; accents: boolean }

export function showMainMenu(root: HTMLElement, options: { airport: string; academyDone?: number; rating?: string; firstVisit?: boolean; commCheck?: CommCheckRadio; onFeedback(): void }): () => void {
  const entry = airportEntry(options.airport) ?? AIRPORTS[0]!;
  const codes = AIRPORTS.map((a) => a.icao.toUpperCase()).join('  ');
  const strips = options.firstVisit ? `
      <nav class="strip-bay menu-primary" aria-label="Get started">
        <a class="strip strip-trn menu-take-position" href="#/tutorial" data-cta="tutorial">
          <span class="strip-cs">Tutorial</span>
          <span class="strip-text"><b>Land one. Launch one.</b><br>Learn the radar and aircraft commands with a coach</span>
          <span class="strip-meta">LLBG · 12<br>Guided</span>
        </a>
        <a class="strip strip-arr" href="#/play" data-cta="take-position">
          <span class="strip-cs">Take position</span>
          <span class="strip-text"><b>Skip ahead to a live watch</b><br>Pick an airport, a position and the traffic</span>
          <span class="strip-meta">${codes}<br>Ready</span>
        </a>
        <a class="strip strip-dep" href="#/academy" data-cta="academy">
          <span class="strip-cs">Academy</span>
          <span class="strip-text"><b>Practice each clearance</b><br>Build your skills with guided exercises</span>
          <span class="strip-meta">${LESSONS.length}<br>lessons</span>
        </a>
      </nav>
      <ul class="menu-features" aria-label="What you get">
        <li><span class="menu-stamp">ICAO</span>Real phraseology and procedures at Ben Gurion and San Francisco</li>
        <li><span class="menu-stamp">AI</span>Pilots who read back, ask, request and push back</li>
        <li><span class="menu-stamp">PTT</span>Speak your clearances with push to talk, or type them</li>
      </ul>
      <p class="menu-fine">Free to play · No account needed · Desktop Chrome, Edge or Safari</p>` : `
      <nav class="strip-bay menu-primary" aria-label="Main menu">
        <a class="strip strip-trn" href="#/tutorial" data-cta="tutorial">
          <span class="strip-cs">Tutorial</span>
          <span class="strip-text"><b>Land one. Launch one.</b><br>Replay the guided runway 12 watch at Ben Gurion</span>
          <span class="strip-meta">LLBG · 12<br>Guided</span>
        </a>
        <a class="strip strip-arr menu-take-position" href="#/play">
          <span class="strip-cs">Take position</span>
          <span class="strip-text"><b>Start a new watch</b><br>Choose an assignment, airfield and workload</span>
          <span class="strip-meta">${codes}<br>Ready</span>
        </a>
        <a class="strip strip-dep" href="#/academy">
          <span class="strip-cs">Academy</span>
          <span class="strip-text"><b>Train for the next clearance</b><br>Short guided exercises on the live scope</span>
          <span class="strip-meta">${options.academyDone ?? 0} / ${LESSONS.length}<br>${options.rating ?? 'lessons'}</span>
          <span class="menu-progress" aria-hidden="true"><i style="--progress:${Math.round(100 * (options.academyDone ?? 0) / LESSONS.length)}%"></i></span>
        </a>
      </nav>`;
  root.innerHTML = `
  <div class="menu${options.firstVisit ? ' menu-landing' : ''}">
    <div class="menu-scope" aria-hidden="true"></div>
    <div class="menu-shade" aria-hidden="true"></div>
    <div class="menu-body">
      <header class="menu-mast">
        <div class="menu-ident"><span class="menu-sweep" aria-hidden="true"></span><h1 class="menu-title" aria-label="ATC AI Simulator">ATC</h1></div>
        ${options.firstVisit
          ? '<p class="menu-tag">An air traffic control simulator with pilots who answer back. Sequence arrivals, launch departures and keep them apart, by voice or keyboard.</p>'
          : '<p class="menu-tag">Take the frequency. Build the arrival sequence, launch departures, and keep the picture safe.</p>'}
        <p class="menu-live"><i aria-hidden="true"></i> Live traffic · ${entry.icao.toUpperCase()} · ${entry.name}</p>
      </header>
      ${options.firstVisit ? `<div class="menu-comms">
        <ol class="menu-radio" aria-label="A sample exchange on frequency">${SAMPLE.map(([who, text], i) => `<li${who === 'YOU' ? ' class="atc"' : ''} style="--i:${i}"><b>${who}</b><span>${text}</span></li>`).join('')}</ol>
        ${options.commCheck ? '<button type="button" class="menu-comm-check" data-comm-check aria-live="polite"><b>Comm check</b><span>Hear the frequency</span></button>' : ''}
      </div>` : ''}
      ${strips}
      <nav class="menu-utility" aria-label="Operations resources">
        <a href="#/briefing"><b>Airport briefing</b><span>Charts, runways and procedures</span></a>
        <a href="#/transcripts" aria-label="Logs · Shift records"><b>Shift records</b><span>Review stored transcripts</span></a>
        <button type="button" data-feedback aria-label="Send feedback or request a feature"><b>Send report</b><span>Feedback and feature requests</span></button>
      </nav>
      <nav class="menu-discover" aria-label="Explore the simulator">
        <a href="/how-to-play.html">How to play</a>
        <a href="/airports/ksfo.html">San Francisco</a>
        <a href="/airports/llbg.html">Ben Gurion</a>
        <a href="https://games.atcaisimulator.com/?ref=atc" data-cta="more-games">More games</a>
      </nav>
    </div>
    <p class="menu-foot">Airport data from openScope and the Israeli eAIP. <a href="#/terms">Terms</a> · <a href="#/privacy">Privacy</a></p>
  </div>`;

  let disposed = false, raf = 0;
  let scope: RadarScope | null = null;
  loadAirport(entry.icao).then((airport) => {
    if (disposed) return;
    const sim = createSim(airport, { realism: 'low', seed: Date.now() % 100000, arrivalsPerHour: 40, departuresPerHour: 0, initialTraffic: false });
    // Pre-roll ten minutes so the scope is busy from the first frame.
    for (let i = 0; i < 600; i++) sim.step(1);
    sim.timeScale = 3;
    const sc = createRadarScope(root.querySelector<HTMLElement>('.menu-scope')!, airport, { realism: sim.realism, rangeNm: 32 });
    scope = sc;
    sim.on('tick', (states, t) => sc.update(states, t));
    let last = performance.now();
    const loop = () => {
      const now = performance.now();
      sim.step(Math.min(0.25, (now - last) / 1000));
      last = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
  }).catch(() => { /* the menu works without its backdrop */ });

  // Comm check: the sample exchange read aloud, the same way a game's radio sounds. The click is
  // also the gesture the browser wants before it lets the page make sound.
  let radio: ReturnType<typeof createRadio> | null = null;
  const commButton = root.querySelector<HTMLButtonElement>('[data-comm-check]');
  const lines = [...root.querySelectorAll<HTMLElement>('.menu-radio li')];
  const resetCheck = () => {
    radio?.destroy(); radio = null;
    lines.forEach((li) => li.classList.remove('on-air'));
    if (commButton) { commButton.dataset.state = ''; commButton.querySelector('span')!.textContent = 'Hear the frequency'; }
  };
  commButton?.addEventListener('click', () => {
    const o = options.commCheck;
    if (!o) return;
    if (radio) { resetCheck(); return; }
    trackEvent('landing_cta', { which: 'comm-check' });
    if (o.provider === 'openai') primeRadioPlayback();
    commButton.dataset.state = 'on-air'; commButton.querySelector('span')!.textContent = 'Loud and clear';
    const messages: RadioMessage[] = SAMPLE.map(([who, text], i) => ({ id: -1 - i, timeS: 0, from: who === 'YOU' ? 'atc' : 'pilot', callsign: 'ELY312', text, speech: text, kind: who === 'YOU' ? 'command' : i === 0 ? 'checkin' : 'readback', ...(who === 'YOU' ? {} : { accent: 'israeli' }) }));
    radio = createRadio({ enabled: true, provider: o.provider, pace: o.pace, noise: o.noise, accents: o.accents, onError: () => { /* falls back to browser voices by itself */ },
      onLine: (msg, onAir) => {
        lines[-1 - msg.id]?.classList.toggle('on-air', onAir);
        if (!onAir && msg.id === messages.at(-1)!.id) resetCheck();
      } });
    for (const msg of messages) radio.push(msg);
  });

  root.querySelector<HTMLElement>('a.strip')?.focus();
  root.querySelector('[data-feedback]')?.addEventListener('click', options.onFeedback);
  root.querySelectorAll<HTMLElement>('[data-cta]').forEach((link) => link.addEventListener('click', () => trackEvent('landing_cta', { which: link.dataset.cta! })));
  const disposeNavigation = mountMenuNavigation(root);
  return () => { disposed = true; cancelAnimationFrame(raf); resetCheck(); disposeNavigation(); scope?.destroy(); root.replaceChildren(); };
}
